import { Link } from "react-router";
import { useGetIdentity, useRefresh, useLoading, useTranslate } from "ra-core";
import {
  Settings,
  User,
  Moon,
  Sun,
  RefreshCw,
  LoaderCircle,
  Check,
} from "lucide-react";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useTheme } from "@/components/admin/use-theme";
import { UserMenu } from "@/components/admin/user-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Sale } from "../providers/fakerest/dataGenerator/types";

export const MobileHeader = () => {
  const config = useConfigurationContext();
  const { data: identity } = useGetIdentity();
  const user = identity as Sale | undefined;
  const { theme, setTheme } = useTheme();
  const refresh = useRefresh();
  const loading = useLoading();
  const translate = useTranslate();

  const logoUrl = config.favicon;
  const companyName = config.companyName || config.title || "Finturf";

  const handleRefresh = () => {
    refresh();
  };

  return (
    <header className="sticky top-0 z-40 bg-background border-b border-border">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Logo and Company Name */}
        <Link to="/" className="flex items-center gap-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="w-7 h-7 rounded object-contain"
            />
          ) : (
            <div className="w-7 h-7 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-semibold text-xs">
                {companyName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-base font-semibold">{companyName}</span>
        </Link>

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          {/* Refresh button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className="h-9 w-9"
          >
            {loading ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="sr-only">Refresh</span>
          </Button>

          {/* Theme toggle dropdown - matches desktop styling */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="sr-only">
                  {translate("ra.action.toggle_theme")}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTheme("light")}>
                {translate("crm.theme.light", { _: "Light" })}
                <Check
                  className={cn(
                    "ml-auto h-4 w-4",
                    theme !== "light" && "hidden",
                  )}
                />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")}>
                {translate("crm.theme.dark", { _: "Dark" })}
                <Check
                  className={cn(
                    "ml-auto h-4 w-4",
                    theme !== "dark" && "hidden",
                  )}
                />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("system")}>
                {translate("crm.theme.system", { _: "System" })}
                <Check
                  className={cn(
                    "ml-auto h-4 w-4",
                    theme !== "system" && "hidden",
                  )}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu with avatar */}
          <UserMenu>
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/profile">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/settings">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
          </UserMenu>
        </div>
      </div>
    </header>
  );
};
